import { apiFetch } from "@/lib/api";

/** Standard Django envelope; list endpoints add `meta`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** A firm email template — a row is present for every seedable code, whether the
 * firm has customized it (`isCustomized`) or it's still the seeded default. */
export interface EmailTemplate {
  /** null when the firm hasn't saved an override yet (still the seed row). */
  id: string | null;
  code: string;
  groupName: string;
  subject: string;
  body: string;
  isCustomized: boolean;
}

interface ApiEmailTemplate {
  id: string | null;
  code: string;
  group_name: string;
  subject: string;
  body: string;
  is_customized: boolean;
}

function toEmailTemplate(t: ApiEmailTemplate): EmailTemplate {
  return {
    id: t.id,
    code: t.code,
    groupName: t.group_name,
    subject: t.subject,
    body: t.body,
    isCustomized: t.is_customized,
  };
}

export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  const { data } = await apiFetch<Envelope<ApiEmailTemplate[]>>("/firm/email-templates/");
  return data.map(toEmailTemplate);
}

export async function getEmailTemplate(code: string): Promise<EmailTemplate> {
  const { data } = await apiFetch<Envelope<ApiEmailTemplate>>(
    `/firm/email-templates/${code}/`,
  );
  return toEmailTemplate(data);
}

export async function saveEmailTemplate(
  code: string,
  payload: { subject: string; body: string },
): Promise<EmailTemplate> {
  const { data } = await apiFetch<Envelope<ApiEmailTemplate>>(
    `/firm/email-templates/${code}/`,
    { method: "PATCH", json: { code, subject: payload.subject, body: payload.body } },
  );
  return toEmailTemplate(data);
}

export async function restoreEmailTemplate(code: string): Promise<EmailTemplate> {
  const { data } = await apiFetch<Envelope<ApiEmailTemplate>>(
    `/firm/email-templates/${code}/restore/`,
    { method: "POST" },
  );
  return toEmailTemplate(data);
}

/** Merge tokens the editor can insert; the server renders these as `{token}`. */
export const EMAIL_MERGE_TOKENS = [
  "company_name",
  "client_name",
  "salutation",
  "link",
  "portal_url",
  "email_signature",
  "files",
] as const;

/** Codes whose save the server blocks if a required token is dropped; the editor
 * mirrors these client-side so a bad save fails fast before the round-trip. */
export const CRITICAL_TOKENS: Record<string, string[]> = {
  portal_onboarding_invite: ["link"],
};
